import { type LeadFormDataPhase } from "../../LeadAddPage/components/lead-form-data.ts"

type MilestoneTimelineIssue =
  | "startsBeforePlan"
  | "endsAfterPlan"
  | "endBeforeStart"

interface PlanningWindow {
  start_date?: string | null
  end_date?: string | null
}

interface MilestoneTimelineWarning {
  milestone: LeadFormDataPhase
  issues: MilestoneTimelineIssue[]
}

/** Reduces a stored date to a `YYYY-MM-DD` key, or `null` when it cannot. */
type DayKeyReader = (value?: string | null) => string | null

const isoDayKey: DayKeyReader = (value) => {
  const match = /^(\d{4}-\d{2}-\d{2})(?:T|$)/.exec(value ?? "")
  return match ? match[1] : null
}

/**
 * Each comparison needs both of its own operands and is silent otherwise, so a
 * lead planned on one edge only is checked on that edge only, and a milestone
 * with a single date is never judged against the date it does not have. ISO
 * day keys order lexically, so the boundaries compare as plain strings.
 */
const milestoneTimelineIssues = (
  milestone: LeadFormDataPhase | null | undefined,
  planning: PlanningWindow | null | undefined,
  toDayKey: DayKeyReader = isoDayKey
): MilestoneTimelineIssue[] => {
  const start = toDayKey(milestone?.start_date)
  const end = toDayKey(milestone?.end_date)
  const planStart = toDayKey(planning?.start_date)
  const planEnd = toDayKey(planning?.end_date)

  const issues: MilestoneTimelineIssue[] = []
  if (start && planStart && start < planStart) issues.push("startsBeforePlan")
  if (end && planEnd && end > planEnd) issues.push("endsAfterPlan")
  if (start && end && end < start) issues.push("endBeforeStart")

  return issues
}

const milestonesTimelineWarnings = (
  milestones: LeadFormDataPhase[] | null | undefined,
  planning: PlanningWindow | null | undefined,
  toDayKey: DayKeyReader = isoDayKey
): MilestoneTimelineWarning[] =>
  (milestones ?? []).flatMap((milestone) => {
    const issues = milestoneTimelineIssues(milestone, planning, toDayKey)
    return issues.length ? [{ milestone, issues }] : []
  })

export {
  type DayKeyReader,
  type MilestoneTimelineIssue,
  type MilestoneTimelineWarning,
  type PlanningWindow,
  isoDayKey,
  milestoneTimelineIssues,
  milestonesTimelineWarnings
}
